/* The season's trips on Home (Oct 2026). Since 24 Oct (mock Home_1 change 4) a row of
   small cards under "Popular trips" - each with its lowest price, cheapest first, swiped
   sideways; a card opens the trip's own page and "All trips ›" opens /trips. The Drivers
   page uses the same row with drivers' fares, and its "All trips ›" opens the sheet of
   trips (TripsSheet), lowest price or shortest distance first (Fix_B change 4).
   Which trips, and the season's title, come from Admin > Pricing > Season trips; the
   prices are worked out here from the cars Home already loaded, so no extra request. */

import { useEffect, useMemo, useState } from "react";
import { apiRequest } from "../../utils/api";
import {
  TRIP_TEXT, seasonTrip, tripText, tripWhenText, tripPrices, cardPrice, rupees,
} from "../../data/seasonTrips";

// One request per visit, shared by every page that asks.
let pending = null;
export function useSeasonTripsConfig() {
  const [cfg, setCfg] = useState(null);
  useEffect(() => {
    let live = true;
    pending = pending || apiRequest("/config/season-trips")
      .then((r) => r?.data || null)
      .catch(() => { pending = null; return null; });
    pending.then((d) => { if (live) setCfg(d); });
    return () => { live = false; };
  }, []);
  return cfg;
}

/* The price in bold inside the sentence, wherever the language puts it. */
export function fillPrice(text, price, className = "text-[13.5px] font-extrabold text-[#3F5E00]") {
  const [a, b] = text.split("\u0000");
  return <>{a}<b className={className}>{price}</b>{b}</>;
}

/* A picture for each trip's card: the airport, a city, the coast, else a temple. */
const tripIcon = (t) => (/airport/.test(t.slug) ? "✈️" : t.slug === "vizag-araku" ? "🏖️"
  : /hyderabad|guntur/.test(t.slug) ? "🏙️" : "🛕");

/** The row: "Popular trips" and "All trips ›", then a small card a trip - its place, how
    long and its lowest price (the distance until prices are known). `priced` as for
    TripsSheet. Cars and Drivers both use it. */
export function TripsRow({ lang, trips, priced, onOpen, onAll }) {
  const rows = [...trips].sort((a, b) => (priced[a.slug]?.price ?? Infinity) - (priced[b.slug]?.price ?? Infinity) || a.km - b.km);
  if (!rows.length) return null;
  return (
    <section>
      <div className="flex items-baseline justify-between gap-2 px-0.5">
        <h2 className="text-[13px] font-extrabold text-[#0F172A]">{tripText(TRIP_TEXT.popular, lang)}</h2>
        {onAll && (
          <a href="/trips" onClick={(e) => { if (e.metaKey || e.ctrlKey) return; e.preventDefault(); onAll(); }}
            className="text-[11px] font-extrabold text-[#5E8700] whitespace-nowrap">{tripText(TRIP_TEXT.allLink, lang)}</a>
        )}
      </div>
      <div className="mt-2 flex gap-2 overflow-x-auto scrollbar-hide snap-x scroll-px-3 -mx-3 px-3 pb-1">
        {rows.map((t) => {
          const p = priced[t.slug];
          return (
            <a key={t.slug} href={`/trips/${t.slug}`} onClick={(e) => { if (e.metaKey || e.ctrlKey) return; e.preventDefault(); onOpen(t); }}
              className="snap-start shrink-0 w-[124px] rounded-2xl bg-white border border-[#E5E7EB] p-2.5 shadow-[0_2px_10px_rgba(15,23,42,0.04)] active:scale-[0.98]">
              <span aria-hidden="true" className="w-[34px] h-[34px] rounded-[10px] bg-[#F1F7E3] flex items-center justify-center text-[17px]">{tripIcon(t)}</span>
              <span className="block mt-2 text-[11px] font-extrabold text-[#0F172A] leading-snug truncate">{tripText(t.dest || t.name, lang)}</span>
              <span className="block text-[9.5px] text-[#64748B] leading-snug truncate">
                {tripWhenText(t, lang)} · {p
                  ? <b className="text-[10.5px] font-extrabold text-[#3F5E00]">{rupees(p.price)}</b>
                  : tripText(TRIP_TEXT.km, lang, { km: t.km.toLocaleString("en-IN") })}
              </span>
            </a>
          );
        })}
      </div>
    </section>
  );
}

/** The sheet of trips: lowest price or shortest distance first, a roomy row each. `priced`
    holds each trip's lowest price and what it is for ({ word, price }). */
export function TripsSheet({ lang, title, sub, trips, priced, onOpen, onClose, onAll }) {
  const [order, setOrder] = useState("PRICE");
  // The sheet closes with Escape too, besides its own button and the shade behind it.
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  const sorted = useMemo(() => {
    const priceOf = (t) => priced[t.slug]?.price ?? Infinity;
    return [...trips].sort((a, b) => (order === "PRICE" ? priceOf(a) - priceOf(b) || a.km - b.km : a.km - b.km || priceOf(a) - priceOf(b)));
  }, [trips, priced, order]);
  const seg = (key, word) => (
    <button type="button" onClick={() => setOrder(key)} aria-pressed={order === key}
      className={`flex-1 min-h-[34px] rounded-full text-[11px] font-extrabold ${order === key ? "bg-white text-[#3F5E00] shadow-sm" : "text-[#475569]"}`}>
      {tripText(word, lang)}
    </button>
  );
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center" role="dialog" aria-modal="true" aria-label={title}>
      <button type="button" aria-label={tripText(TRIP_TEXT.close, lang)} onClick={onClose} className="absolute inset-0 bg-[#0F172A]/45" />
      <div className="relative w-full max-w-lg bg-white rounded-t-[22px] px-4 pt-2.5 pb-[calc(14px+env(safe-area-inset-bottom,0px))] max-h-[85vh] overflow-y-auto">
        <span aria-hidden="true" className="block mx-auto w-10 h-1 rounded bg-[#CBD5E1]" />
        <div className="flex items-start gap-2 mt-2">
          <div className="min-w-0 flex-1">
            <h2 className="text-[15px] font-extrabold text-[#0F172A]">{title}</h2>
            <p className="text-[11px] text-[#64748B] leading-snug">{sub}</p>
          </div>
          <button type="button" onClick={onClose} aria-label={tripText(TRIP_TEXT.close, lang)}
            className="w-9 h-9 rounded-full bg-[#F1F5F9] text-[#334155] text-[16px] font-bold shrink-0">×</button>
        </div>
        <div className="flex gap-1 bg-[#F1F5F9] rounded-full p-[3px] mt-2.5">
          {seg("PRICE", TRIP_TEXT.byPrice)}
          {seg("KM", TRIP_TEXT.byDistance)}
        </div>
        <div className="mt-1">
          {sorted.map((t) => {
            const p = priced[t.slug];
            const meta = [tripWhenText(t, lang), tripText(TRIP_TEXT.km, lang, { km: t.km.toLocaleString("en-IN") })];
            return (
              <a key={t.slug} href={`/trips/${t.slug}`}
                onClick={(e) => { if (e.metaKey || e.ctrlKey) return; e.preventDefault(); onClose(); onOpen(t); }}
                className="flex items-center gap-3 py-3 border-b border-[#F1F5F9] last:border-0 active:bg-[#F8FAFC]">
                <span className="min-w-0 flex-1">
                  <span className="block text-[13px] font-extrabold text-[#0F172A] leading-snug">
                    {tripText(t.dest || t.name, lang)}
                    {t.tag && <span className="ml-1.5 align-middle text-[9.5px] font-extrabold text-[#B45309] bg-[#FEF3C7] rounded-md px-1.5 py-[1px]">{tripText(TRIP_TEXT.tag[t.tag], lang)}</span>}
                  </span>
                  <span className="block text-[10.5px] text-[#64748B] mt-1">{meta.join(" · ")}</span>
                </span>
                <span className="shrink-0 text-right text-[10px] text-[#64748B] min-w-[68px]">
                  {p ? <>{p.word}<b className="block text-[14px] font-extrabold text-[#3F5E00] mt-0.5">{rupees(p.price)}</b></> : null}
                </span>
              </a>
            );
          })}
        </div>
        {onAll && (
          <a href="/trips" onClick={(e) => { if (e.metaKey || e.ctrlKey) return; e.preventDefault(); onClose(); onAll(); }}
            className="mt-2 flex items-center justify-center min-h-[42px] rounded-xl border border-[#7BAE00] text-[#3F5E00] text-[12.5px] font-extrabold">
            {tripText(TRIP_TEXT.allLink, lang)}
          </a>
        )}
      </div>
    </div>
  );
}

/** On a laptop (mock Laptop_1): the trips as a card beside the search - the cheapest
    first, with prices, and All trips. `priced` as for TripsSheet. */
export function TripsCard({ lang, trips, priced, onOpen, onAll }) {
  const rows = [...trips].sort((a, b) => (priced[a.slug]?.price ?? Infinity) - (priced[b.slug]?.price ?? Infinity)).slice(0, 4);
  if (!rows.length) return null;
  return (
    <div className="bg-white border border-[#DCEBB8] rounded-2xl px-4 py-3.5 shadow-[0_2px_10px_rgba(15,23,42,0.04)]">
      <div className="flex items-center justify-between gap-2">
        <p className="text-[15px] font-extrabold text-[#0F172A] flex items-center gap-1.5"><span aria-hidden="true">🛕</span>{tripText(TRIP_TEXT.popular, lang)}</p>
        {onAll && <a href="/trips" onClick={(e) => { if (e.metaKey || e.ctrlKey) return; e.preventDefault(); onAll(); }} className="text-[12.5px] font-extrabold text-[#5E8700] hover:underline">{tripText(TRIP_TEXT.allLink, lang)}</a>}
      </div>
      <div className="mt-1">
        {rows.map((t) => {
          const p = priced[t.slug];
          return (
            <a key={t.slug} href={`/trips/${t.slug}`} onClick={(e) => { if (e.metaKey || e.ctrlKey) return; e.preventDefault(); onOpen(t); }}
              className="flex items-center justify-between gap-3 py-2 border-b border-[#F1F5F9] last:border-0 hover:bg-[#F8FBF1] -mx-1 px-1 rounded-lg">
              <span className="min-w-0">
                <span className="block text-[13.5px] font-bold text-[#0F172A] truncate">{tripText(t.dest || t.name, lang)}</span>
                <span className="block text-[11.5px] text-[#64748B]">{tripWhenText(t, lang)} · {tripText(TRIP_TEXT.km, lang, { km: t.km.toLocaleString("en-IN") })}</span>
              </span>
              {p && <b className="shrink-0 text-[14px] font-extrabold text-[#3F5E00]">{rupees(p.price)}</b>}
            </a>
          );
        })}
      </div>
    </div>
  );
}

export default function SeasonTrips({ cars, cityPricing, calculateFare, lang, onOpen, onOpenIndex, asCard = false }) {
  const cfg = useSeasonTripsConfig();
  const trips = useMemo(() => (cfg?.slugs || []).map(seasonTrip).filter(Boolean), [cfg]);
  const priced = useMemo(() => {
    if (!cars?.length || !cityPricing) return {};
    const out = {};
    for (const t of trips) {
      const p = cardPrice(t, tripPrices(t, cars, cityPricing, calculateFare));
      if (p) out[t.slug] = { price: p.price, word: tripText(TRIP_TEXT.group[p.group], lang) };
    }
    return out;
  }, [trips, cars, cityPricing, calculateFare, lang]);

  if (!trips.length) return null;
  if (asCard) return <TripsCard lang={lang} trips={trips} priced={priced} onOpen={onOpen} onAll={onOpenIndex} />;
  return <TripsRow lang={lang} trips={trips} priced={priced} onOpen={onOpen} onAll={onOpenIndex} />;
}
